import React, { useMemo, useRef } from "react";
import { random, useRandom, useRandomSelectionFrom } from "../../util/random";

import ContentfulClient from "../../lib/contentful_client/ContentfulClient";
import Draggable from "../../components/svg/Draggable";
import FoundryPhysicalProduct from "../../lib/foundry_client/models/FoundryPhysicalProduct";
import { GetStaticProps } from "next";
import Loader from "../../components/Loader";
import { PageFadeWrapper } from "../../components/PageWrapper";
import ProductCard from "../../components/shop/ProductCard";
import Settings from "../../settings";
import Star from "../../public/elements/icons/combinedStar-test.svg";
import usePhysicalProducts from "../../hooks/usePhysicalProducts";
import { useWindowSize } from "@uidotdev/usehooks";

const remap = (value, low1, high1, low2, high2) => {
  return (
    low2 +
    (value - low1) * Math.min(1.0, Math.max(0, high2 - low2) / (high1 - low1))
  );
};

interface Props {
  physicalProducts: FoundryPhysicalProduct[];
}

function ShopIndex(props: Props) {
  const physicalProducts = usePhysicalProducts(props.physicalProducts);
  const mainRef = useRef(null);
  const size = useWindowSize();

  let starSize = Math.ceil(remap(size.width || 2000, 400, 2000, 150, 400));
  let starCount = Math.ceil(remap(size.width || 2000, 400, 2000, 12, 12));
  let fadeHeight = Math.ceil(remap(size.width || 2000, 400, 2000, 30, 60));
  let productHeight = Math.ceil(remap(size.width || 2000, 400, 2000, 20, 29));

  const availableProducts = physicalProducts.filter(
    (p) => p.maxQuantity > p.orderedQuantity
  );
  const soldOutProducts = physicalProducts.filter(
    (p) => p.maxQuantity <= p.orderedQuantity
  );

  return physicalProducts.length === 0 ? (
    <div className="w-full pt-48 flex items-center justify-center">
      <Loader size={90} />
    </div>
  ) : (
    <PageFadeWrapper>
      <div
        id="home"
        className="overflow-hidden absolute masked w-screen"
        style={{
          height: `calc(${fadeHeight}vh + 8rem)`,
        }}
      >
        <div
          ref={mainRef}
          className=""
          style={{
            height: `calc(${fadeHeight}vh + 16rem)`,
            width: "calc(100vw + 16rem)",
            transform: "translate(-8rem, -8rem)",
          }}
        >
          {Array(starCount)
            .fill(0)
            .map((_, idx) => {
              return (
                <DraggablePrintedStar
                  constraintRef={mainRef}
                  key={idx}
                  myX={remap(idx, 0, starCount - 1, -0.1, 0.9)}
                  starSize={starSize}
                />
              );
            })}
        </div>
      </div>
      <div
        className="container-padded grid grid-cols-2 lg:grid-cols-3 gap-12 md:gap-24 relative mt-12"
        style={{ marginTop: `calc(${productHeight}vh)` }}
      >
        {availableProducts.map((product) => (
          <ProductCard product={product} />
        ))}
      </div>
      <div className="pt-24 pb-24 lg:pb-36 xl:pb-48 container-padded grid grid-cols-2 lg:grid-cols-3 gap-12 md:gap-24 relative">
        {soldOutProducts.map((product) => (
          <ProductCard product={product} />
        ))}
      </div>
    </PageFadeWrapper>
  );
}

function DraggablePrintedStar({
  constraintRef,
  myX,
  myY,
  starSize,
}: {
  constraintRef: unknown;
  myX?: number;
  myY?: number;
  starSize: number;
}) {
  const x = myX != undefined ? useMemo(() => myX, []) : useRandom(0, 0.8);
  const y = myY != undefined ? useMemo(() => myY, []) : useRandom(0.2, 0.6);
  const yScroll = useRandom(0.2, 0.4);
  const rotate = useRandom(-45, 45);
  const ONE_START_OPACITIES = [40, 50, 60, 70, 80, 90, 100];
  const opacity = useRandomSelectionFrom(ONE_START_OPACITIES);
  const color = useRandomSelectionFrom(PRINTED_STAR_COLORS);
  const color2 = useRandomSelectionFrom(OTHER_PRINTED_STAR_COLORS);

  return (
    <Draggable
      constraintRef={constraintRef}
      minX={x}
      maxX={x}
      maxXVel={() => 500}
      minY={y}
      maxY={y}
      yScroll={yScroll}
      rotate={rotate}
      calculateYVal={() => random(0.05, 0.4) * -window.innerHeight}
    >
      <Star
        width={starSize}
        style={{
          "--color-star-main": `var(--color-${color})`,
          "--color-star-bg": `var(--color-${color2})`,
          "--opacity-star-main": `${opacity}%`,
        }}
      />
    </Draggable>
  );
}

export const getStaticProps: GetStaticProps<Props> = async () => {
  const physicalProducts = await ContentfulClient.getPhysicalProducts();

  return {
    props: { physicalProducts: JSON.parse(JSON.stringify(physicalProducts)) },
    revalidate: Settings.revalidate,
  };
};

ShopIndex.displayName = "ShopIndex";
export default ShopIndex;

const PRINTED_STAR_COLORS = ["yellow", "blue", "mint", "pink"];
const OTHER_PRINTED_STAR_COLORS = [...PRINTED_STAR_COLORS];
